import { useMemo } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { type Listing } from '@pkgs/api/models';
import { Headline } from '@ev-tech/ui-core-components/Headline';

import { getSpecificLivingAreaLabel } from '@/utils/translationLabels';
import { getPropertyDetails, hasEnergyInfo } from '@/utils/propertyDataUtils';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';

import { PropertyFact } from './PropertyFact';
import { prepareDetailsColumns } from './prepareDetailsColumns';

import styles from './PropertyDetails.module.css';

export const PropertyDetailsExtraInfo = ({ listing }: { listing: Listing }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { language, measurementSystem } = useSettings();
  const showsConstructionYear = hasEnergyInfo(listing);

  const propertyDetails = useMemo(
    () => getPropertyDetails(listing, getSpecificLivingAreaLabel(listing), showsConstructionYear, t, language, measurementSystem),
    [listing, showsConstructionYear, t, language, measurementSystem],
  );

  const [leftColumn, rightColumn] = prepareDetailsColumns(propertyDetails);

  return (
    <div className={styles.detailsContainer} data-testid="expose-property-details-extra-info">
      <Headline size="h5" as="span" label={t('search.expose.propertyDetails.headline')} />
      <div className={styles.columnContainer}>
        <ul className={styles.list}>
          {leftColumn.map((item, index) => (
            <PropertyFact {...item} key={index} isLast={index === leftColumn.length - 1} />
          ))}
        </ul>
        <ul className={styles.list}>
          {rightColumn.map((item, index) => (
            <PropertyFact {...item} key={index} isLast={index === rightColumn.length - 1} />
          ))}
        </ul>
      </div>
    </div>
  );
};
